import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Ball, BallComponent } from '../ball/ball.component';

const INANE_WAFFLE = [
  'Dodgy rack?',
  'Was that a push shot?',
  'The ball has left the table...',
  `It's gone in-off!`,
  'You may need some chalk on that cue...',
  'What a dreadful miscue.',
  `Oh, that's an unfortunate kick.`,
  `We're a few balls short of a full rack :(`,
  `You need to work on your positioning, son.`,
];

@Component({
  selector: 'lib-error-ui',
  imports: [CommonModule, BallComponent],
  templateUrl: './error-ui.component.html',
  styleUrl: './error-ui.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorUiComponent {
  /**
   * Typically an Error object, an HttpErrorResponse object, or a string.
   * If none of these, an 'unknown error' message will be shown instead.
   * Set to an empty string to provide an error message via content projection
   * instead.
   */
  readonly error = input.required<NonNullable<unknown>>();
  /**
   * An explicit status code to show.  If not set, and [error] is set to an
   * instance of HttpErrorResponse, then the status from the error response
   * object is used instead.
   *
   * If no status is given and the [error] input is not set to an instance of
   * HttpErrorResponse, then no status code balls will be shown.
   */
  readonly status = input<number | undefined>(undefined);

  readonly smallWaffle = input<boolean>(false);

  readonly errorMessage = computed(() => toErrorMessage(this.error()));
  readonly statusCodeDigits = computed(() =>
    getStatusCodeDigits(this.status(), this.error()),
  );

  readonly inaneWaffle =
    INANE_WAFFLE[Math.floor(Math.random() * INANE_WAFFLE.length)];
}

function toErrorMessage(error: NonNullable<unknown> | string): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof HttpErrorResponse) {
    return (
      extractErrorMessage(error.error) ||
      error.message ||
      error.statusText ||
      (error.status
        ? `Error ${error.status}`
        : 'Unknown HTTP error - check your network connection')
    );
  }
  return extractErrorMessage(error) || 'An unknown error occurred 🤷';
}

function extractErrorMessage(error: unknown): string | undefined {
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.length
  ) {
    return error.message;
  }
  return undefined;
}

function getStatusCodeDigits(
  explicitStatus: number | undefined,
  error: unknown,
): Ball[] {
  const status =
    explicitStatus ??
    (error instanceof HttpErrorResponse ? error.status : undefined);
  if (status) {
    return `${Math.floor(status)}`.split('') as Ball[];
  }
  return [];
}
